import { Navigate } from "react-router-dom";
import RouteGuard from "../guards/RouteGuard";
import AuthGuard from "../components/authentication/AuthGuard";
import GuestGuard from "../components/authentication/GuestGuard";
import DashboardLayout from "../components/Layouts/DashboardLayout";
import { LoginPortal } from "../pages/Authentication/LoginPortal";
import LazyLoader from "./routes-loader";

const routes = [
  {
    path: "/",
    element: <Navigate to="dashboard" />,
  },
  {
    path: "login",
    element: (
      <GuestGuard>
        <LoginPortal />
      </GuestGuard>
    ),
  },

  {
    path: "",
    element: (
      <AuthGuard>
        <DashboardLayout />
      </AuthGuard>
    ),
    children: [
      {
        path: "loan/details/:loanId",
        element: <LazyLoader.LoanDetailView />,
        requiresAuth: true,
      },
      {
        path: "loan/details/:loanId/:tabId",
        element: <LazyLoader.LoanDetailView />,
        requiresAuth: true,
      },
    ],
  },

  {
    path: "dashboard",
    element: (
      <AuthGuard>
        <DashboardLayout />
      </AuthGuard>
    ),
    children: [
      {
        path: "",
        element: <LazyLoader.DashboardSaaS />,
      },
      {
        path: "account/:tabId",
        element: <LazyLoader.AccountProfile />,
      },
    ],
  },

  {
    path: "forbidden",
    element: <LazyLoader.Error403 />,
  },
  {
    path: "*",
    element: <LazyLoader.Error404 />,
  },

  // ----------------------------------------------------------------
  // ADMIN ROUTES  (under /admin/...)
  // ----------------------------------------------------------------
  {
    path: "admin",
    element: (
      <AuthGuard>
        <DashboardLayout />
      </AuthGuard>
    ),
    children: [
      // Investments
      {
        path: "investment",
        element: <LazyLoader.Investment />,
        requiresAuth: true,
      },
      // Loans
      {
        path: "loan",
        element: <LazyLoader.Loan />,
        requiresAuth: true,
      },
      {
        path: "loan/create",
        element: <LazyLoader.LoanForm />,
        requiresAuth: true,
      },
      {
        path: "loan/details/:loanId",
        element: <LazyLoader.LoanDetailView />,
        requiresAuth: true,
      },
      // Transactions
      {
        path: "transactions",
        element: <LazyLoader.AdminTransactions />,
        requiresAuth: true,
      },
      // Wallets
      {
        path: "wallets",
        element: <LazyLoader.AdminWallets />,
        requiresAuth: true,
      },
      // Banks
      {
        path: "banks",
        element: <LazyLoader.AdminBanks />,
        requiresAuth: true,
      },
      // Reports
      {
        path: "reports",
        element: <LazyLoader.AdminReports />,
        requiresAuth: true,
      },
      // AutoInvest treasury
      {
        path: "autoinvest",
        element: <LazyLoader.AdminAutoInvest />,
        requiresAuth: true,
      },
      // Recovery lane — defaulted loans + bad-debt collections
      {
        path: "recovery",
        element: <LazyLoader.AdminRecovery />,
        requiresAuth: true,
      },
      // Business Profiles
      {
        path: "business",
        element: <LazyLoader.AdminBusiness />,
        requiresAuth: true,
      },
      // Activity Log
      {
        path: "activity",
        element: <LazyLoader.AdminActivity />,
        requiresAuth: true,
      },
      // Notifications
      {
        path: "notifications",
        element: <LazyLoader.AdminNotifications />,
        requiresAuth: true,
      },
      // Platform Settings
      {
        path: "platform-settings",
        element: <LazyLoader.AdminPlatformSettings />,
        requiresAuth: true,
      },
      // Devices
      {
        path: "devices",
        element: <LazyLoader.DeviceCatalog />,
        requiresAuth: true,
      },
      {
        path: "devices/detail/:deviceId",
        element: <LazyLoader.DeviceDetail />,
        requiresAuth: true,
      },
      {
        path: "device-orders",
        element: <LazyLoader.DeviceOrders />,
        requiresAuth: true,
      },

      // ==============================================================
      // Phase 6: Manual Transactions
      // ==============================================================
      {
        path: "manual-disbursements",
        element: <LazyLoader.ManualDisbursements />,
        requiresAuth: true,
      },
      {
        path: "manual-repayments",
        element: <LazyLoader.ManualRepayments />,
        requiresAuth: true,
      },
      {
        path: "manual-deposits",
        element: <LazyLoader.AdminManualDeposits />,
        requiresAuth: true,
      },
      {
        path: "manual-withdrawals",
        element: <LazyLoader.AdminManualWithdrawals />,
        requiresAuth: true,
      },
      {
        path: "company-accounts",
        element: <LazyLoader.CompanyAccounts />,
        requiresAuth: true,
      },
      {
        path: "reconciliation",
        element: <LazyLoader.Reconciliation />,
        requiresAuth: true,
      },
    ],
  },

  // ----------------------------------------------------------------
  // Legacy top-level redirects
  // ----------------------------------------------------------------
  {
    path: "banks",
    element: (
      <AuthGuard>
        <DashboardLayout />
      </AuthGuard>
    ),
    children: [
      {
        path: "/banks",
        element: (
          <RouteGuard requiredPermissions={[]}>
            <LazyLoader.AdminBanks />
          </RouteGuard>
        ),
        requiresAuth: true,
      },
    ],
  },
  {
    path: "institutions",
    element: (
      <AuthGuard>
        <DashboardLayout />
      </AuthGuard>
    ),
    children: [
      {
        path: "/institutions",
        element: (
          <RouteGuard requiredPermissions={[]}>
            <LazyLoader.AdminBusiness />
          </RouteGuard>
        ),
        requiresAuth: true,
      },
    ],
  },

  // ----------------------------------------------------------------
  // ACCOUNT
  // ----------------------------------------------------------------
  {
    path: "account",
    element: (
      <AuthGuard>
        <DashboardLayout />
      </AuthGuard>
    ),
    children: [
      {
        path: "profile",
        element: <LazyLoader.AdminProfile />,
        requiresAuth: true,
      },
    ],
  },

  // ----------------------------------------------------------------
  // USERS
  // ----------------------------------------------------------------
  {
    path: "users",
    element: (
      <AuthGuard>
        <DashboardLayout />
      </AuthGuard>
    ),
    children: [
      {
        path: "/users",
        element: (
          <RouteGuard requiredPermissions={[]}>
            <LazyLoader.Users />
          </RouteGuard>
        ),
        requiresAuth: true,
      },
      {
        path: "details/:id",
        element: (
          <RouteGuard requiredPermissions={[]}>
            <LazyLoader.AdminUserDetail />
          </RouteGuard>
        ),
        requiresAuth: true,
      },
      {
        path: "deletion-requests",
        element: (
          <RouteGuard requiredPermissions={[]}>
            <LazyLoader.DeletionRequests />
          </RouteGuard>
        ),
        requiresAuth: true,
      },
    ],
  },
];

export default routes;